import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2, Camera, X, Sparkles, ChevronRight, ChevronLeft } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useVehicleTypes } from '@/hooks/useVehicleTypes';
import { useAddVehicle } from '@/hooks/useVehicles';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { vehicleSchema, type VehicleFormData } from '@/lib/validations/vehicle';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useTranslation } from 'react-i18next';
import { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useUploadVehicleDocument } from '@/hooks/useVehicleDocuments';
import { Badge } from '@/components/ui/badge';

interface AddVehicleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddVehicleDialog = ({ open, onOpenChange }: AddVehicleDialogProps) => {
  const { t, i18n } = useTranslation();
  const { vehicleTypes, getVehicleTypeName } = useVehicleTypes();
  const { addVehicle, isAdding } = useAddVehicle();
  const { uploadDocumentAsync } = useUploadVehicleDocument();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [registrationFrontImage, setRegistrationFrontImage] = useState<string | null>(null);
  const [registrationBackImage, setRegistrationBackImage] = useState<string | null>(null);
  const [registrationFrontFile, setRegistrationFrontFile] = useState<File | null>(null);
  const [registrationBackFile, setRegistrationBackFile] = useState<File | null>(null);
  const [frontAnalyzed, setFrontAnalyzed] = useState(false);
  const [backAnalyzed, setBackAnalyzed] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedExpiry, setExtractedExpiry] = useState<string | null>(null);
  const [extractedFirstDate, setExtractedFirstDate] = useState<string | null>(null);
  const fileInputFrontRef = useRef<HTMLInputElement>(null);
  const fileInputBackRef = useRef<HTMLInputElement>(null);

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      case 'de': return de;
      case 'it': return it;
      default: return enUS;
    }
  };

  const form = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      vehicle_type_id: '',
      plate_number: '',
      vin: '',
      brand: '',
      model: '',
      year: undefined,
      last_maintenance_date: undefined,
      next_maintenance_date: undefined,
    },
  });

  const handleImageCapture = async (
    event: React.ChangeEvent<HTMLInputElement>,
    side: 'front' | 'back'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageDataUrl = reader.result as string;
      if (side === 'front') {
        setRegistrationFrontImage(imageDataUrl);
        setRegistrationFrontFile(file);
      } else {
        setRegistrationBackImage(imageDataUrl);
        setRegistrationBackFile(file);
      }
    };
    reader.readAsDataURL(file);

    // Auto-analyze after upload
    await analyzeImage(file, side);
  };

  const analyzeImage = async (file: File, side: 'front' | 'back') => {
    setIsExtracting(true);
    
    toast({
      title: t('fleet.extraction.analyzing'),
      description: side === 'front' ? t('fleet.extraction.analyzingFront') : t('fleet.extraction.analyzingBack'),
    });

    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      await new Promise<void>((resolve, reject) => {
        reader.onload = async () => {
          try {
            const base64Image = (reader.result as string).split(',')[1];
            
            const { data: functionData, error: functionError } = await supabase.functions.invoke(
              'extract-vehicle-data',
              {
                body: { imageBase64: base64Image }
              }
            );

            if (functionError) throw functionError;
            
            // Reject if not a registration card
            if (functionData?.success === false) {
              if (functionData.error === 'NOT_A_REGISTRATION_CARD') {
                toast({
                  title: t('fleet.extraction.notRegistrationCard'),
                  description: t('fleet.extraction.notRegistrationCardDesc'),
                  variant: 'destructive',
                });
                // Remove the rejected image
                handleRemoveImage(side);
                resolve();
                return;
              }
              throw new Error(functionData.error || 'Extraction failed');
            }
            
            if (!functionData?.data) {
              throw new Error('No data returned from extraction');
            }

            const extractedData = functionData.data;

            // Fill form from extracted data
            if (extractedData.plate_number) {
              form.setValue('plate_number', extractedData.plate_number);
            }
            if (extractedData.vin) {
              form.setValue('vin', extractedData.vin);
            }
            if (extractedData.brand) {
              form.setValue('brand', extractedData.brand);
            }
            if (extractedData.model) {
              form.setValue('model', extractedData.model);
            }
            if (extractedData.year) {
              form.setValue('year', extractedData.year);
            }
            // Stash OCR-only fields for later save
            if (extractedData.registration_expiry) {
              setExtractedExpiry(extractedData.registration_expiry);
            }
            if (extractedData.registration_first_date) {
              setExtractedFirstDate(extractedData.registration_first_date);
            }

            // Update analysis status
            if (side === 'front') {
              setFrontAnalyzed(true);
            } else {
              setBackAnalyzed(true);
            }

            toast({
              title: t('fleet.extraction.success'),
              description: side === 'front' ? t('fleet.extraction.frontComplete') : t('fleet.extraction.backComplete'),
            });

            resolve();
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
      });
    } catch (error) {
      console.error('Error extracting vehicle data:', error);
      toast({
        title: t('fleet.extraction.error'),
        description: error instanceof Error ? error.message : t('fleet.extraction.error'),
        variant: 'destructive',
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleRemoveImage = (side: 'front' | 'back') => {
    if (side === 'front') {
      setRegistrationFrontImage(null);
      setRegistrationFrontFile(null);
      setFrontAnalyzed(false);
      if (fileInputFrontRef.current) {
        fileInputFrontRef.current.value = '';
      }
    } else {
      setRegistrationBackImage(null);
      setRegistrationBackFile(null);
      setBackAnalyzed(false);
      if (fileInputBackRef.current) {
        fileInputBackRef.current.value = '';
      }
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      // Validate step 1 (vehicle type required)
      const isValid = await form.trigger('vehicle_type_id');
      if (isValid) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      // Step 2 is optional, always allow to proceed
      setCurrentStep(3);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  const onSubmit = async (data: VehicleFormData) => {
    try {
      const vehicleId = await addVehicle({
        vehicle_type_id: data.vehicle_type_id,
        plate_number: data.plate_number.toUpperCase(),
        vin: data.vin || null,
        brand: data.brand || null,
        model: data.model || null,
        year: data.year ? Number(data.year) : null,
        last_maintenance_date: data.last_maintenance_date?.toISOString() || null,
        next_maintenance_date: data.next_maintenance_date?.toISOString() || null,
      });

      // Persist OCR-only fields (registration_expiry, first_date, verified_at) via secured edge function
      if (vehicleId && (extractedExpiry || extractedFirstDate || frontAnalyzed || backAnalyzed)) {
        supabase.functions.invoke('save-vehicle-document-data', {
          body: {
            vehicleId,
            data: {
              plate_number: data.plate_number.toUpperCase(),
              vin: data.vin || null,
              brand: data.brand || null,
              model: data.model || null,
              year: data.year ? Number(data.year) : null,
              registration_first_date: extractedFirstDate,
              registration_expiry: extractedExpiry,
            },
          },
        }).catch(err => console.error('Failed to save OCR data:', err));
      }

      // Close dialog and reset form
      onOpenChange(false);
      form.reset();
      setCurrentStep(1);
      setRegistrationFrontImage(null);
      setRegistrationBackImage(null);
      setRegistrationFrontFile(null);
      setRegistrationBackFile(null);
      setFrontAnalyzed(false);
      setBackAnalyzed(false);
      setExtractedExpiry(null);
      setExtractedFirstDate(null);

      // Upload documents in background
      if (registrationFrontFile && vehicleId) {
        uploadDocumentAsync({
          file: registrationFrontFile,
          vehicleId: vehicleId,
          documentType: 'registration_card'
        }).catch(err => console.error('Background upload failed:', err));
      }
      
      if (registrationBackFile && vehicleId) {
        uploadDocumentAsync({
          file: registrationBackFile,
          vehicleId: vehicleId,
          documentType: 'registration_card'
        }).catch(err => console.error('Background upload failed:', err));
      }
    } catch (error) {
      // Error handling is done in the hooks
    }
  };

  const [lastMaintenanceCalendarOpen, setLastMaintenanceCalendarOpen] = useState(false);
  const [nextMaintenanceCalendarOpen, setNextMaintenanceCalendarOpen] = useState(false);

  const resetDialog = () => {
    form.reset();
    setCurrentStep(1);
    setRegistrationFrontImage(null);
    setRegistrationBackImage(null);
    setRegistrationFrontFile(null);
    setRegistrationBackFile(null);
    setFrontAnalyzed(false);
    setBackAnalyzed(false);
    setExtractedExpiry(null);
    setExtractedFirstDate(null);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        resetDialog();
      }
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
            {t('fleet.dialogs.addVehicle')}
          </DialogTitle>
        </DialogHeader>

        {/* Wizard Stepper */}
        <div className="flex items-center justify-between gap-2 p-4 bg-[#EBF4F8] rounded-lg">
          <div className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              currentStep >= 1 ? 'bg-[#0880AE] text-white' : 'bg-[#DBE2EA] text-[#5A6B7A]'
            }`}>
              {currentStep > 1 ? '✓' : '1'}
            </div>
            <span className={`text-xs md:text-sm transition-colors ${
              currentStep >= 1 ? 'text-[#2D3748] font-medium' : 'text-[#5A6B7A]'
            }`}>
              {t('fleet.wizard.step1')}
            </span>
          </div>
          <ChevronRight className="w-4 h-4 text-[#87ADD4]" />
          <div className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              currentStep >= 2 ? 'bg-[#0880AE] text-white' : 'bg-[#DBE2EA] text-[#5A6B7A]'
            }`}>
              {currentStep > 2 ? '✓' : '2'}
            </div>
            <span className={`text-xs md:text-sm transition-colors ${
              currentStep >= 2 ? 'text-[#2D3748] font-medium' : 'text-[#5A6B7A]'
            }`}>
              {t('fleet.wizard.step2')}
            </span>
          </div>
          <ChevronRight className="w-4 h-4 text-[#87ADD4]" />
          <div className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              currentStep >= 3 ? 'bg-[#0880AE] text-white' : 'bg-[#DBE2EA] text-[#5A6B7A]'
            }`}>
              3
            </div>
            <span className={`text-xs md:text-sm transition-colors ${
              currentStep >= 3 ? 'text-[#2D3748] font-medium' : 'text-[#5A6B7A]'
            }`}>
              {t('fleet.wizard.step3')}
            </span>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            
            {/* Step 1: Vehicle Type Selection */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="text-sm text-[#5A6B7A] mb-4">
                  {t('fleet.wizard.step1Description')}
                </div>
                <FormField
                  control={form.control}
                  name="vehicle_type_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                        {t('fleet.dialogs.vehicleType')} {t('fleet.dialogs.required')}
                      </FormLabel>
                      <FormControl>
                        <Select 
                          onValueChange={field.onChange} 
                          value={field.value}
                        >
                          <SelectTrigger className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-[#0880AE]">
                            <SelectValue placeholder={t('fleet.dialogs.selectVehicleType')} />
                          </SelectTrigger>
                          <SelectContent>
                            {vehicleTypes.map((type) => (
                              <SelectItem key={type.id} value={type.id}>
                                {getVehicleTypeName(type.trailer_type)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage className="text-[#FF7171] text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Step 2: Registration Card Photos (Optional) */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="text-sm text-[#5A6B7A] mb-4">
                  {t('fleet.wizard.step2Description')}
                </div>

                {/* Progress stepper for photos */}
                <div className="flex items-center justify-center gap-2 p-4 bg-[#EBF4F8] rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      frontAnalyzed ? 'bg-[#14A38B] text-white' : registrationFrontFile ? 'bg-[#FFBF07] text-[#2D3748]' : 'bg-[#DBE2EA] text-[#5A6B7A]'
                    }`}>
                      {frontAnalyzed ? '✓' : '1'}
                    </div>
                    <span className="text-sm text-[#2D3748]">{t('fleet.extraction.step1')}</span>
                  </div>
                  <div className="w-8 h-0.5 bg-[#DBE2EA]" />
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      backAnalyzed ? 'bg-[#14A38B] text-white' : registrationBackFile ? 'bg-[#FFBF07] text-[#2D3748]' : 'bg-[#DBE2EA] text-[#5A6B7A]'
                    }`}>
                      {backAnalyzed ? '✓' : '2'}
                    </div>
                    <span className="text-sm text-[#2D3748]">{t('fleet.extraction.step2')}</span>
                  </div>
                </div>

                {/* Front side */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-[#5A6B7A]">{t('fleet.dialogs.captureRegistrationFront')}</span>
                    {registrationFrontFile && (
                      <Badge className={frontAnalyzed ? 'bg-[#14A38B]' : 'bg-[#FFBF07] text-[#2D3748]'}>
                        {frontAnalyzed ? '✓ ' + t('fleet.extraction.frontComplete') : '⏳ ' + t('fleet.extraction.analyzing')}
                      </Badge>
                    )}
                  </div>
                  <input
                    ref={fileInputFrontRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleImageCapture(e, 'front')}
                    className="hidden"
                  />
                  {!registrationFrontImage ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputFrontRef.current?.click()}
                      disabled={isExtracting}
                      className="w-full h-auto min-h-[120px] flex-col gap-2 border-2 border-[#0880AE] hover:bg-[#EBF4F8]"
                    >
                      <Camera className="h-8 w-8 text-[#0880AE]" />
                      <span className="text-sm font-medium">{t('fleet.dialogs.captureRegistrationFront')}</span>
                    </Button>
                  ) : (
                    <div className="relative rounded-lg border-2 border-[#0880AE] overflow-hidden">
                      <img 
                        src={registrationFrontImage} 
                        alt="Registration card - Front" 
                        className="w-full h-48 object-cover"
                      />
                      {isExtracting && registrationFrontFile && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <div className="flex flex-col items-center gap-2 text-white">
                            <Sparkles className="h-8 w-8 animate-pulse" />
                            <p className="text-sm font-medium">{t('fleet.extraction.analyzing')}</p>
                          </div>
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveImage('front')}
                        disabled={isExtracting}
                        className="absolute top-2 right-2 bg-white/90 hover:bg-white text-[#FF7171] rounded-full"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Back side */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-[#5A6B7A]">{t('fleet.dialogs.captureRegistrationBack')}</span>
                    {registrationBackFile && (
                      <Badge className={backAnalyzed ? 'bg-[#14A38B]' : 'bg-[#FFBF07] text-[#2D3748]'}>
                        {backAnalyzed ? '✓ ' + t('fleet.extraction.backComplete') : '⏳ ' + t('fleet.extraction.analyzing')}
                      </Badge>
                    )}
                  </div>
                  <input
                    ref={fileInputBackRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleImageCapture(e, 'back')}
                    className="hidden"
                  />
                  {!registrationBackImage ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputBackRef.current?.click()}
                      disabled={isExtracting}
                      className="w-full h-auto min-h-[120px] flex-col gap-2 border-2 border-[#87ADD4] hover:bg-[#EBF4F8]"
                    >
                      <Camera className="h-8 w-8 text-[#87ADD4]" />
                      <span className="text-sm font-medium">{t('fleet.dialogs.captureRegistrationBack')}</span>
                    </Button>
                  ) : (
                    <div className="relative rounded-lg border-2 border-[#87ADD4] overflow-hidden">
                      <img 
                        src={registrationBackImage} 
                        alt="Registration card - Back" 
                        className="w-full h-48 object-cover"
                      />
                      {isExtracting && registrationBackFile && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <div className="flex flex-col items-center gap-2 text-white">
                            <Sparkles className="h-8 w-8 animate-pulse" />
                            <p className="text-sm font-medium">{t('fleet.extraction.analyzing')}</p>
                          </div>
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveImage('back')}
                        disabled={isExtracting}
                        className="absolute top-2 right-2 bg-white/90 hover:bg-white text-[#FF7171] rounded-full"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="p-4 bg-[#0880AE]/10 border border-[#0880AE]/20 rounded-lg">
                  <p className="text-sm text-[#2D3748]">
                    <strong>{t('fleet.extraction.optional')}</strong> {t('fleet.extraction.optionalDesc')}
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Vehicle Information Form */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="text-sm text-[#5A6B7A] mb-4">
                  {t('fleet.wizard.step3Description')}
                </div>

                <FormField
                  control={form.control}
                  name="plate_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                        {t('fleet.dialogs.plateNumber')} {t('fleet.dialogs.required')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t('fleet.dialogs.plateNumberPlaceholder')}
                          className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-[#0880AE]"
                        />
                      </FormControl>
                      <FormMessage className="text-[#FF7171] text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                        {t('fleet.dialogs.vin')} {t('fleet.dialogs.required')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ''}
                          placeholder={t('fleet.dialogs.vinPlaceholder')}
                          className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-[#0880AE]"
                        />
                      </FormControl>
                      <FormMessage className="text-[#FF7171] text-xs" />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="brand"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                          {t('fleet.dialogs.brand')} {t('fleet.dialogs.required')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ''}
                            placeholder={t('fleet.dialogs.brandPlaceholder')}
                            className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-[#0880AE]"
                          />
                        </FormControl>
                        <FormMessage className="text-[#FF7171] text-xs" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="model"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                          {t('fleet.dialogs.model')} {t('fleet.dialogs.required')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ''}
                            placeholder={t('fleet.dialogs.modelPlaceholder')}
                            className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-[#0880AE]"
                          />
                        </FormControl>
                        <FormMessage className="text-[#FF7171] text-xs" />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="year"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                        {t('fleet.dialogs.year')} {t('fleet.dialogs.required')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                          placeholder={t('fleet.dialogs.yearPlaceholder')}
                          className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-[#0880AE]"
                        />
                      </FormControl>
                      <FormMessage className="text-[#FF7171] text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_maintenance_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                        {t('fleet.dialogs.lastMaintenance')}
                      </FormLabel>
                      <Popover open={lastMaintenanceCalendarOpen} onOpenChange={setLastMaintenanceCalendarOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal border-[#DBE2EA]",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP", { locale: getDateLocale() })
                              ) : (
                                <span>{t('fleet.dialogs.lastMaintenancePlaceholder')}</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                              setLastMaintenanceCalendarOpen(false);
                            }}
                            disabled={(date) => date > new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage className="text-[#FF7171] text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="next_maintenance_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                        {t('fleet.dialogs.nextMaintenance')}
                      </FormLabel>
                      <Popover open={nextMaintenanceCalendarOpen} onOpenChange={setNextMaintenanceCalendarOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal border-[#DBE2EA]",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP", { locale: getDateLocale() })
                              ) : (
                                <span>{t('fleet.dialogs.nextMaintenancePlaceholder')}</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                              setNextMaintenanceCalendarOpen(false);
                            }}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage className="text-[#FF7171] text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <DialogFooter>
              <div className="flex gap-2 w-full justify-between">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    className="border-[#DBE2EA]"
                  >
                    {t('common.cancel')}
                  </Button>
                  {currentStep > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePrevious}
                      className="border-[#DBE2EA]"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      {t('common.previous')}
                    </Button>
                  )}
                </div>
                
                {currentStep < 3 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={currentStep === 1 && !form.watch('vehicle_type_id')}
                    className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748]"
                  >
                    {t('common.next')}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isAdding}
                    className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748]"
                  >
                    {isAdding ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('fleet.dialogs.addingVehicle')}
                      </>
                    ) : (
                      t('fleet.dialogs.add')
                    )}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
